import { useRef, useState, type ReactNode } from 'react';
import { ChevronDown, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import type { PlayEventScoreInput } from '@/services/playEvent';
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
  Label,
} from '@/components/ui';
import { cn } from '@/lib/utils';

export interface PlayEventMatchScoreInitialValues {
  winnerSide?: 'a' | 'b' | null;
  sets?: Array<{ a: number; b: number }>;
}

interface PlayEventMatchScoreFormProps {
  sideALabel: string;
  sideBLabel: string;
  isLoading?: boolean;
  mode?: 'record' | 'update';
  initialValues?: PlayEventMatchScoreInitialValues;
  /** When only one slot has a player (opponent TBD / feeder). */
  singlePopulatedSide?: 'a' | 'b' | null;
  onSubmit: (data: PlayEventScoreInput) => void;
}

interface SetRow {
  a: string;
  b: string;
}

const DEFAULT_SET_COUNT = 2;
const MAX_SET_COUNT = 5;

function createEmptySetRow(): SetRow {
  return { a: '', b: '' };
}

function createDefaultSetRows(): SetRow[] {
  return Array.from({ length: DEFAULT_SET_COUNT }, () => createEmptySetRow());
}

function rowsFromSets(sets?: Array<{ a: number; b: number }>): SetRow[] {
  if (!sets?.length) {
    return createDefaultSetRows();
  }

  return sets.map((set) => ({
    a: String(set.a),
    b: String(set.b),
  }));
}

function parseSetScore(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number.parseInt(trimmed, 10);
  if (Number.isNaN(parsed) || parsed < 0) {
    return null;
  }
  return parsed;
}

export function buildSetsFromRows(rows: SetRow[]): Array<{ a: number; b: number }> {
  const sets: Array<{ a: number; b: number }> = [];

  for (const row of rows) {
    const scoreA = parseSetScore(row.a);
    const scoreB = parseSetScore(row.b);
    if (scoreA != null && scoreB != null) {
      sets.push({ a: scoreA, b: scoreB });
    }
  }

  return sets;
}

export function findIncompleteSetRows(rows: SetRow[]): number[] {
  const incomplete: number[] = [];

  rows.forEach((row, index) => {
    const hasA = row.a.trim().length > 0;
    const hasB = row.b.trim().length > 0;
    if (hasA !== hasB) {
      incomplete.push(index + 1);
    }
  });

  return incomplete;
}

function PlayEventSetScoreChips({
  sets,
  sideALabel,
  sideBLabel,
}: {
  sets: Array<{ a: number; b: number }>;
  sideALabel: string;
  sideBLabel: string;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {sets.map((set, index) => (
        <span
          key={`set-chip-${index}`}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-medium tabular-nums"
        >
          <span className="text-muted-foreground">Set {index + 1}</span>
          <span>{set.a}</span>
          <span className="text-muted-foreground">-</span>
          <span>{set.b}</span>
          <span className="sr-only">
            {sideALabel} {set.a}, {sideBLabel} {set.b}
          </span>
        </span>
      ))}
    </div>
  );
}

export function PlayEventMatchScoreForm({
  sideALabel,
  sideBLabel,
  isLoading = false,
  mode = 'record',
  initialValues,
  singlePopulatedSide = null,
  onSubmit,
}: PlayEventMatchScoreFormProps) {
  const isUpdate = mode === 'update';
  const savedWinnerSide = initialValues?.winnerSide ?? null;
  const savedSets = initialValues?.sets ?? [];
  const [showChangeWinner, setShowChangeWinner] = useState(false);
  const initialRows = rowsFromSets(savedSets);
  const setRowsRef = useRef<SetRow[]>(initialRows);
  const [setRows, setSetRows] = useState<SetRow[]>(initialRows);

  const commitSetRows = (next: SetRow[]) => {
    setRowsRef.current = next;
    setSetRows(next);
  };

  const getRowsForSubmit = () => setRowsRef.current;

  const updateSetRow = (index: number, side: 'a' | 'b', value: string) => {
    commitSetRows(
      setRowsRef.current.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [side]: value } : row
      )
    );
  };

  const addSetRow = () => {
    if (setRowsRef.current.length >= MAX_SET_COUNT) return;
    commitSetRows([...setRowsRef.current, createEmptySetRow()]);
  };

  const removeSetRow = (index: number) => {
    if (setRowsRef.current.length <= 1) return;
    commitSetRows(setRowsRef.current.filter((_, rowIndex) => rowIndex !== index));
  };

  const validateRowsForSubmit = (): boolean => {
    const incompleteSets = findIncompleteSetRows(getRowsForSubmit());
    if (incompleteSets.length > 0) {
      toast.error(
        `Enter both scores for set ${incompleteSets.join(', ')}, or clear the row before saving.`
      );
      return false;
    }
    return true;
  };

  const buildWinnerPayload = (winnerSide: 'a' | 'b'): PlayEventScoreInput => {
    const sets = buildSetsFromRows(getRowsForSubmit());
    const payload: PlayEventScoreInput = {
      result: 'winner',
      winnerSide,
    };
    if (sets.length > 0) {
      payload.score = { sets };
    }
    return payload;
  };

  const buildSetScoresPayload = (winnerSide: 'a' | 'b'): PlayEventScoreInput => ({
    result: 'winner',
    winnerSide,
    score: { sets: buildSetsFromRows(getRowsForSubmit()) },
  });

  const buildWalkoverPayload = (winnerSide: 'a' | 'b'): PlayEventScoreInput => {
    const sets = buildSetsFromRows(getRowsForSubmit());
    const payload: PlayEventScoreInput = {
      result: 'walkover',
      winnerSide,
    };
    if (sets.length > 0) {
      payload.score = { sets };
    }
    return payload;
  };

  const submitWinner = (winnerSide: 'a' | 'b') => {
    if (!validateRowsForSubmit()) return;
    onSubmit(buildWinnerPayload(winnerSide));
  };

  const submitSetScores = (winnerSide: 'a' | 'b') => {
    if (!validateRowsForSubmit()) return;
    onSubmit(buildSetScoresPayload(winnerSide));
  };

  const submitWalkover = (winnerSide: 'a' | 'b') => {
    if (!validateRowsForSubmit()) return;
    onSubmit(buildWalkoverPayload(winnerSide));
  };

  const awaitingOpponentLabel =
    singlePopulatedSide === 'a' ? sideALabel : singlePopulatedSide === 'b' ? sideBLabel : null;

  if (singlePopulatedSide && awaitingOpponentLabel) {
    return (
      <div className="mt-4 rounded-lg border border-dashed border-border bg-muted/20 p-3">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {isUpdate ? 'Update result' : 'Advance player'}
        </p>
        <p className="mb-3 text-xs text-muted-foreground">
          Opponent not assigned yet. Advance {awaitingOpponentLabel} to the next round.
        </p>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={isLoading}
          onClick={() =>
            onSubmit({ result: 'walkover', winnerSide: singlePopulatedSide })
          }
        >
          {isUpdate ? 'Save' : 'Advance'} {awaitingOpponentLabel}
        </Button>
      </div>
    );
  }

  const winnerLabel =
    savedWinnerSide === 'a' ? sideALabel : savedWinnerSide === 'b' ? sideBLabel : null;

  if (isUpdate && savedWinnerSide) {
    return (
      <div className="mt-4 space-y-3 rounded-lg border border-dashed border-border bg-muted/20 p-3">
        <div className="rounded-md border border-border bg-background px-3 py-2.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Result
          </p>
          <p className="mt-1 text-sm font-medium text-foreground">{winnerLabel} wins</p>
          {savedSets.length > 0 ? (
            <div className="mt-2">
              <PlayEventSetScoreChips
                sets={savedSets}
                sideALabel={sideALabel}
                sideBLabel={sideBLabel}
              />
            </div>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">No set scores recorded yet.</p>
          )}
        </div>

        <SetScoresCollapsibleSection
          title="Set scores"
          setRows={setRows}
          sideALabel={sideALabel}
          sideBLabel={sideBLabel}
          isLoading={isLoading}
          onUpdateRow={updateSetRow}
          onAddRow={addSetRow}
          onRemoveRow={removeSetRow}
          footer={
            <Button
              type="button"
              size="sm"
              className="mt-4"
              disabled={isLoading}
              onClick={() => submitSetScores(savedWinnerSide)}
            >
              Save scores
            </Button>
          }
        />

        <div>
          <button
            type="button"
            className="text-xs font-medium text-primary hover:underline"
            onClick={() => setShowChangeWinner((current) => !current)}
          >
            {showChangeWinner ? 'Hide change winner' : 'Change winner'}
          </button>
          {showChangeWinner ? (
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant={savedWinnerSide === 'a' ? 'default' : 'secondary'}
                disabled={isLoading}
                onClick={() => submitWinner('a')}
              >
                {sideALabel} wins
              </Button>
              <Button
                type="button"
                size="sm"
                variant={savedWinnerSide === 'b' ? 'default' : 'secondary'}
                disabled={isLoading}
                onClick={() => submitWinner('b')}
              >
                {sideBLabel} wins
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isLoading}
                onClick={() => submitWalkover('a')}
              >
                Walkover · {sideALabel}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isLoading}
                onClick={() => submitWalkover('b')}
              >
                Walkover · {sideBLabel}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-lg border border-dashed border-border bg-muted/20 p-3">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Record score
      </p>
      <SetScoresCollapsibleSection
        title="Set scores (optional)"
        setRows={setRows}
        sideALabel={sideALabel}
        sideBLabel={sideBLabel}
        isLoading={isLoading}
        onUpdateRow={updateSetRow}
        onAddRow={addSetRow}
        onRemoveRow={removeSetRow}
      />
      <p className="mt-3 text-xs text-muted-foreground">
        Pick the winner to save the result{buildSetsFromRows(setRows).length > 0 ? ' and set scores' : ''}.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={isLoading}
          onClick={() => submitWinner('a')}
        >
          {sideALabel} wins
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={isLoading}
          onClick={() => submitWinner('b')}
        >
          {sideBLabel} wins
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={isLoading}
          onClick={() => submitWalkover('a')}
        >
          Walkover · {sideALabel}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={isLoading}
          onClick={() => submitWalkover('b')}
        >
          Walkover · {sideBLabel}
        </Button>
      </div>
    </div>
  );
}

function SetScoresCollapsibleSection({
  title,
  setRows,
  sideALabel,
  sideBLabel,
  isLoading,
  onUpdateRow,
  onAddRow,
  onRemoveRow,
  footer,
}: {
  title: string;
  setRows: SetRow[];
  sideALabel: string;
  sideBLabel: string;
  isLoading: boolean;
  onUpdateRow: (index: number, side: 'a' | 'b', value: string) => void;
  onAddRow: () => void;
  onRemoveRow: (index: number) => void;
  footer?: ReactNode;
}) {
  const enteredSetCount = buildSetsFromRows(setRows).length;

  return (
    <Collapsible defaultOpen={false} className="rounded-md border border-border bg-background">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-muted/30"
        >
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {title}
          </span>
          <span className="flex items-center gap-2 text-xs text-muted-foreground">
            {enteredSetCount > 0 ? (
              <span className="font-medium text-foreground">
                {enteredSetCount} set{enteredSetCount === 1 ? '' : 's'}
              </span>
            ) : null}
            <ChevronDown
              className="h-4 w-4 shrink-0 transition-transform [[data-state=open]_&]:rotate-180"
              aria-hidden
            />
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t border-border px-3 pb-3 pt-3">
        <SetScoreEditor
          setRows={setRows}
          sideALabel={sideALabel}
          sideBLabel={sideBLabel}
          isLoading={isLoading}
          onUpdateRow={onUpdateRow}
          onAddRow={onAddRow}
          onRemoveRow={onRemoveRow}
        />
        {footer}
      </CollapsibleContent>
    </Collapsible>
  );
}

function SetScoreEditor({
  setRows,
  sideALabel,
  sideBLabel,
  isLoading,
  onUpdateRow,
  onAddRow,
  onRemoveRow,
}: {
  setRows: SetRow[];
  sideALabel: string;
  sideBLabel: string;
  isLoading: boolean;
  onUpdateRow: (index: number, side: 'a' | 'b', value: string) => void;
  onAddRow: () => void;
  onRemoveRow: (index: number) => void;
}) {
  return (
    <>
      <div
        className="mb-2 hidden gap-3 text-xs font-medium text-muted-foreground sm:grid sm:grid-cols-[4.5rem_1fr_1fr_2rem]"
        aria-hidden
      >
        <span />
        <span className="truncate">{sideALabel}</span>
        <span className="truncate">{sideBLabel}</span>
        <span />
      </div>

      <ul className="space-y-2">
        {setRows.map((row, index) => (
          <li
            key={`set-row-${index}`}
            className="grid grid-cols-1 gap-2 sm:grid-cols-[4.5rem_1fr_1fr_2rem] sm:items-center sm:gap-3"
          >
            <Label className="text-xs text-muted-foreground sm:pt-2">Set {index + 1}</Label>
            <Input
              inputMode="numeric"
              value={row.a}
              onChange={(event) => onUpdateRow(index, 'a', event.target.value)}
              placeholder="0"
              aria-label={`Set ${index + 1} games for ${sideALabel}`}
              className="h-9"
            />
            <Input
              inputMode="numeric"
              value={row.b}
              onChange={(event) => onUpdateRow(index, 'b', event.target.value)}
              placeholder="0"
              aria-label={`Set ${index + 1} games for ${sideBLabel}`}
              className="h-9"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                'h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive',
                setRows.length <= 1 && 'invisible pointer-events-none'
              )}
              disabled={isLoading || setRows.length <= 1}
              onClick={() => onRemoveRow(index)}
              aria-label={`Remove set ${index + 1}`}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </Button>
          </li>
        ))}
      </ul>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="mt-3 h-8 px-2 text-xs"
        disabled={isLoading || setRows.length >= MAX_SET_COUNT}
        onClick={onAddRow}
      >
        <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
        Add set
        {setRows.length >= MAX_SET_COUNT ? ` (max ${MAX_SET_COUNT})` : ''}
      </Button>
    </>
  );
}
